/**
 * @fileoverview The pictures a link carries, as the plant card shows its photographs: one
 * at a time, swiped or stepped with arrows, with a dot for each underneath.
 *
 * A gardener who composed three pictures in the share sheet sent three, and
 * a visitor pages through them the way the gardener does on the card. One
 * picture is simply the picture.
 *
 * Each picture keeps its shape, centred in the column, and is never taller
 * than SHARE_PICTURE_MAX_HEIGHT (a share of the screen's height). A story
 * frame (1080 × 1920) drawn at the column's full width stood taller than a
 * laptop's screen and pushed the gardener's words and the invitation below
 * it out of sight.
 *
 * Where: the share page (SharePage), for a link whose picture the share
 * sheet composed.
 * API: none; the pictures are the share's frame addresses.
 * Settings: SHARE_PICTURE_MAX_HEIGHT (config/tunables/shared).
 * Words (en/es): plants.card.photos.prev, .next, .list, .jump (the card's
 * own words for its photo steps).
 *
 * @module FrameStrip
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { SHARE_PICTURE_MAX_HEIGHT } from '@/config/tunables/shared';

/**
 * A link's pictures, paged one at a time with arrows and dots; one picture is
 * just the picture.
 *
 * @param frames - The pictures' addresses, in the order the sheet made them.
 * @param alt - What they are of, for a screen reader.
 */
export const FrameStrip: React.FC<{ frames: string[]; alt: string }> = ({ frames, alt }) => {
  const { reactTranslate: t } = useTranslation();
  const [selected, setSelected] = React.useState(0);
  /** The sideways-scrolling row of slides, for stepping to a picture and reading a swipe. */
  const scroller = React.useRef<HTMLDivElement>(null);
  const many = frames.length > 1;

  /**
   * Shows one picture, scrolled to smoothly; out-of-range steps stop at the ends.
   *
   * @param index - The picture's place in frames.
   */
  const go = (index: number): void => {
    const next = Math.max(0, Math.min(frames.length - 1, index));
    setSelected(next);
    const el = scroller.current;
    if (el) el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
  };

  /** A swipe moves the selection too, so the dots follow the thumb. */
  const onScroll = (): void => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (index !== selected) setSelected(index);
  };

  if (frames.length === 0) return null;
  if (!many) {
    return (
      <div className="mt-6 flex justify-center">
        <img src={frames[0]} alt={alt} className="h-auto w-auto max-w-full rounded-sm object-contain shadow-md" style={{ maxHeight: SHARE_PICTURE_MAX_HEIGHT }} />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div
        ref={scroller}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {/* A full-width slide each, so a swipe moves one picture; the
            picture centred in it, capped in height and keeping its shape. */}
        {frames.map((src, index) => (
          <div key={src} className="flex w-full shrink-0 snap-center justify-center">
            <img src={src} alt={`${alt} ${index + 1}`} className="h-auto w-auto max-w-full rounded-sm object-contain shadow-md"
              style={{ maxHeight: SHARE_PICTURE_MAX_HEIGHT }} loading={index === 0 ? 'eager' : 'lazy'} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-center gap-2">
        <button type="button" onClick={() => go(selected - 1)} disabled={selected === 0}
          aria-label={t('plants.card.photos.prev')} className="rounded-full p-1.5 hover:bg-muted disabled:opacity-30">
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <ol className="flex items-center gap-1" aria-label={t('plants.card.photos.list')}>
          {frames.map((src, index) => (
            <li key={src} className="flex">
              <button type="button" onClick={() => go(index)} className="flex h-8 items-center px-0.5"
                aria-label={t('plants.card.photos.jump', { n: index + 1, total: frames.length })} aria-current={index === selected ? 'true' : undefined}>
                <span className={cn('block rounded-full transition-all', index === selected ? 'h-2 w-2 bg-foreground' : 'h-1.5 w-1.5 bg-muted-foreground/50')} />
              </button>
            </li>
          ))}
        </ol>
        <button type="button" onClick={() => go(selected + 1)} disabled={selected === frames.length - 1}
          aria-label={t('plants.card.photos.next')} className="rounded-full p-1.5 hover:bg-muted disabled:opacity-30">
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
